import {
  VStack,
  HStack,
  Text,
  Box,
  Flex,
  Icon,
  Spinner,
  SimpleGrid,
  Accordion,
  Separator,
  useDisclosure,
} from "@chakra-ui/react";
import SectionHeader from "../../../CustomUi/SectionHeader";
import { useSchoolsContext } from "../../../../Contexts/schoolContext";
import ClassModal from "./ClassModal";
import { Class, Level } from "../../../../services/Dtos";
import { useEffect, useState } from "react";
import Empty from "../../../CustomUi/Empty";
import { useNavigate } from "react-router-dom";
import useUnsavedChangesPrompt from "../../../../hooks/useUnsavedChangesPrompt";
import useThemeColors from "../../../../hooks/useThemeColors";
import { useStyles } from "../../../../hooks/useStyles";
import ClassDetailsPanel from "./ClassDetailsPanel";
import useIcons from "../../../../hooks/useIcons";
import Cad from "../../../CustomUi/Cad";
import CustomButton from "../../../CustomUi/CustomButton";
import { useConfirmDialog } from "../../../../hooks/useConfirmDialog";
import { getNextOrder } from "../../../../services/Utils";
import { useClassService } from "../../../../hooks/Domain/useClassService";
import { useBreakPoint } from "../../../../hooks/useBreakPoint";

export default function ClassSettings() {
  const {
    levels,
    subjects,
    behavioralSubjects,
    isLoading,
    error,
    accentColor,
    classes,
  } = useSchoolsContext();

  // Use states from the hook
  const {
    selectedClass,
    setSelectedClass,
    classToEdit,
    setClassToEdit,
    classLevel,
    setClassLevel,
    selectedClassSubjects,
    setSelectedClassSubjects,
    selectedClassBehavioralSubjects,
    setSelectedClassBehavioralSubjects,
    unsavedChanges,
    setUnsavedChanges,
  } = useClassService();

  const [accordionIndex] = useState<number | number[]>(0);
  const [mobileView, setMobileView] = useState<
    "levels" | "classes" | "details"
  >("levels");
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const { scrollBar } = useStyles();
  const icons = useIcons();
  const colors = useThemeColors();
  useUnsavedChangesPrompt(unsavedChanges);
  const { confirmUnsavedChanges } = useConfirmDialog();
  const [nextOrder, setNextOrder] = useState(0);

  useEffect(() => {
    setSelectedClass(null);
  }, [accordionIndex]);

  useEffect(() => {
    if (selectedClass) {
      setSelectedClassSubjects(selectedClass.subjects?.map((s) => s.id) || []);
      setSelectedClassBehavioralSubjects(
        selectedClass.behavioralSubjects?.map((s) => s.id) || []
      );
      setUnsavedChanges(false);
    } else {
      setSelectedClassSubjects([]);
      setSelectedClassBehavioralSubjects([]);
      setUnsavedChanges(false);
    }
  }, [selectedClass]);

  const handleTabChange = (level: Level) => {
    setClassLevel(level);
    setUnsavedChanges(false);
    //setSelectedClass(null);
  };

  const handleClassSelect = (classRoom: Class) => {
    if (unsavedChanges) {
      const confirmLeave = confirmUnsavedChanges();
      if (!confirmLeave) {
        return;
      }
    }
    setSelectedClass(classRoom);
  };

  const handleAddClass = () => {
    setClassToEdit(null);

    setNextOrder(getNextOrder(classes));
    onOpen();
  };

  const handleEditClass = (classToEdit: Class) => {
    setClassToEdit(classToEdit);
    setClassLevel(levels.find((l) => l.id === classToEdit.levelId) || null);
    onOpen();
  };

  const { isMobile } = useBreakPoint();

  useEffect(() => {
    if (!isMobile) {
      setMobileView("levels");
      setClassLevel(null);
    }
  }, [isMobile]);

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner size="xl" color={colors.schoolAccent.icon} />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={8} textAlign="center">
        <Text color="red.500">Error loading class settings</Text>
      </Box>
    );
  }

  return (
    <VStack gap={4} h="full">
      <ClassModal
        isOpen={isOpen}
        onClose={onClose}
        classRoomToEdit={classToEdit}
        classLevel={classLevel}
        nextOrder={nextOrder}
      />

      {levels.length > 0 ? (
        <Cad minimal>
          <VStack gap={8} align="stretch" h="full">
            <SectionHeader
              title="Classes and Subjects"
              subtitle="Manage classes, assign subjects, and configure class details for all school levels"
              icon={icons.main.class}
            />

            <SimpleGrid
              columns={{ base: 1, md: 2 }}
              gap={8}
              display={{ base: "none", md: "grid" }}
              overflow="hidden"
              h="full"
            >
              <VStack
                align="stretch"
                gap={6}
                h="full"
                overflowY="auto"
                css={scrollBar}
              >
                <SectionHeader
                  mb={0}
                  title=""
                  buttonText="Add Class"
                  onButtonClick={() => handleAddClass()}
                  buttonIcon={icons.utility.plus}
                  buttonColorScheme={colors.schoolAccent.primary}
                  subtitle="Select a level to manage its classes"
                />

                <Accordion.Root
                  variant="enclosed"
                  collapsible
                  defaultValue={[levels[0].id]}
                >
                  {levels.map((level) => (
                    <Accordion.Item
                      key={level.id}
                      value={level.id}
                      onClick={() => handleTabChange(level)}
                    >
                      <Accordion.ItemTrigger>
                        <HStack justify="space-between" w="full">
                          <Text
                            color={colors.schoolAccent.text}
                            fontWeight="semibold"
                          >
                            {level.levelName}
                          </Text>
                          <Accordion.ItemIndicator />
                        </HStack>
                      </Accordion.ItemTrigger>
                      <Accordion.ItemContent>
                        <Accordion.ItemBody>
                          <VStack gap={2} align="stretch">
                            {classes
                              .filter((c) => c.levelId === level.id)
                              .map((cls) => (
                                <Box
                                  key={cls.id}
                                  p={2}
                                  borderWidth={1}
                                  borderRadius="md"
                                  borderColor={
                                    selectedClass?.id === cls.id
                                      ? colors.brand.primary
                                      : colors.border.subtle
                                  }
                                  bg={
                                    selectedClass?.id === cls.id
                                      ? colors.schoolAccent.focus
                                      : colors.background.tertiary
                                  }
                                  onClick={() => handleClassSelect(cls)}
                                  cursor="pointer"
                                  transition="all 0.2s"
                                  _hover={{
                                    bg:
                                      selectedClass?.id === cls.id
                                        ? colors.schoolAccent.focus
                                        : colors.schoolAccent.hover,
                                    transform: "translateY(-1px)",
                                  }}
                                >
                                  <HStack
                                    justify="space-between"
                                    align="center"
                                  >
                                    <Text>{cls.className}</Text>
                                    {selectedClass?.id === cls.id && (
                                      <CustomButton
                                        size="xs"
                                        variant="outline"
                                        colorPalette={
                                          colors.schoolAccent.primary
                                        }
                                        label="Edit Class Details"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleEditClass(cls);
                                        }}
                                      />
                                    )}
                                  </HStack>
                                </Box>
                              ))}
                          </VStack>
                        </Accordion.ItemBody>
                      </Accordion.ItemContent>
                    </Accordion.Item>
                  ))}
                </Accordion.Root>
              </VStack>

              <VStack
                align="stretch"
                gap={6}
                display={{
                  base: "none",
                  md: "flex",
                }}
                h="full"
                overflow="hidden"
              >
                {selectedClass ? (
                  <Box
                    bg={colors.background.secondary}
                    borderWidth={1}
                    borderColor={colors.border.secondary}
                    borderRadius="lg"
                    p={6}
                    h="full"
                    overflow="auto"
                    css={scrollBar}
                  >
                    <ClassDetailsPanel
                      setSelectedClass={setSelectedClass}
                      selectedClass={selectedClass}
                      subjects={subjects}
                      behavioralSubjects={behavioralSubjects}
                      selectedClassSubjects={selectedClassSubjects}
                      setSelectedClassSubjects={setSelectedClassSubjects}
                      selectedClassBehavioralSubjects={
                        selectedClassBehavioralSubjects
                      }
                      setSelectedClassBehavioralSubjects={
                        setSelectedClassBehavioralSubjects
                      }
                      handleEditClass={handleEditClass}
                      inClassLoading={false}
                      unsavedChanges={unsavedChanges}
                      setUnsavedChanges={setUnsavedChanges}
                      accentColor={accentColor}
                    />
                  </Box>
                ) : (
                  <VStack
                    justify="center"
                    align="center"
                    gap={6}
                    minH="400px"
                    color={colors.text.secondary}
                    textAlign="center"
                  >
                    <Box
                      p={6}
                      borderRadius="full"
                      bg={colors.schoolAccent.focus}
                      color={colors.schoolAccent.icon}
                    >
                      <Icon as={icons.utility.chalkboard} boxSize={12} />
                    </Box>
                    <VStack gap={3}>
                      <Text
                        fontSize="xl"
                        fontWeight="semibold"
                        color={colors.text.primary}
                      >
                        Select a class to begin
                      </Text>
                      <Text fontSize="md" maxW="300px">
                        Choose a class from the list on the left to assign or
                        update its subjects.
                      </Text>
                    </VStack>
                  </VStack>
                )}
              </VStack>
            </SimpleGrid>

            {/* Mobile View */}
            <Box
              display={{ base: "block", md: "none" }}
              h="calc(100vh - 250px)"
              overflowY="auto"
              css={scrollBar}
            >
              {mobileView === "levels" && (
                <VStack align="stretch" gap={6}>
                  <SectionHeader
                    mb={0}
                    title=""
                    buttonText="Add Class"
                    onButtonClick={() => handleAddClass()}
                    buttonIcon={icons.utility.plus}
                    buttonColorScheme={colors.schoolAccent.primary}
                    subtitle="Select a level to manage its classes"
                  />

                  <Accordion.Root variant="enclosed" collapsible>
                    {levels.map((level) => (
                      <Accordion.Item key={level.id} value={level.id}>
                        <Accordion.ItemTrigger>
                          <HStack justify="space-between" w="full">
                            <Text
                              color={colors.schoolAccent.text}
                              fontWeight="semibold"
                            >
                              {level.levelName}
                            </Text>
                            <Accordion.ItemIndicator />
                          </HStack>
                        </Accordion.ItemTrigger>
                        <Accordion.ItemContent>
                          <Accordion.ItemBody>
                            <VStack gap={2} align="stretch">
                              {classes
                                .filter((c) => c.levelId === level.id)
                                .map((cls) => (
                                  <Box
                                    key={cls.id}
                                    p={2}
                                    borderWidth={1}
                                    borderRadius="md"
                                    borderColor={colors.border.subtle}
                                    bg={colors.background.tertiary}
                                    onClick={() => {
                                      setSelectedClass(cls);
                                      setMobileView("details");
                                    }}
                                    cursor="pointer"
                                    transition="all 0.2s"
                                    _hover={{
                                      bg: colors.schoolAccent.hover,
                                      transform: "translateY(-1px)",
                                    }}
                                  >
                                    <HStack
                                      justify="space-between"
                                      align="center"
                                    >
                                      <Text>{cls.className}</Text>
                                    </HStack>
                                  </Box>
                                ))}
                            </VStack>
                          </Accordion.ItemBody>
                        </Accordion.ItemContent>
                      </Accordion.Item>
                    ))}
                  </Accordion.Root>
                </VStack>
              )}

              {mobileView === "details" && selectedClass && (
                <VStack align="stretch" gap={4}>
                  <CustomButton
                    variant="outline"
                    colorPalette={colors.schoolAccent.primary}
                    label="Back to Classes"
                    onClick={() => setMobileView("levels")}
                    icon={icons.utility.chevronLeft}
                  />
                  <Separator />

                  <ClassDetailsPanel
                    setSelectedClass={setSelectedClass}
                    selectedClass={selectedClass}
                    subjects={subjects}
                    behavioralSubjects={behavioralSubjects}
                    selectedClassSubjects={selectedClassSubjects}
                    setSelectedClassSubjects={setSelectedClassSubjects}
                    selectedClassBehavioralSubjects={
                      selectedClassBehavioralSubjects
                    }
                    setSelectedClassBehavioralSubjects={
                      setSelectedClassBehavioralSubjects
                    }
                    handleEditClass={handleEditClass}
                    inClassLoading={false}
                    unsavedChanges={unsavedChanges}
                    setUnsavedChanges={setUnsavedChanges}
                    accentColor={accentColor}
                  />
                </VStack>
              )}
            </Box>
          </VStack>
        </Cad>
      ) : (
        <Cad minimal>
          <Empty
            hasButton
            message="Something will show up here when you set up School Levels"
            buttonText="Go to Levels Settings"
            handleClick={() => navigate("/settings/level")}
          />
        </Cad>
      )}
    </VStack>
  );
}
